import type { ServerTiming } from './serverTiming';

/**
 * Module-level, dependency-free timing observer for `ApiClient`.
 *
 * Per AC-32, timing data is delivered *only* through this opt-in subscribe
 * hook — never by changing an existing `ApiClient` method's signature,
 * return type, or thrown error. No call site in `web/src` needs editing.
 * Nothing subscribes today; this exists so a future diagnostics panel (see
 * `docs/backlog/deferred.md`) can hang
 * off it without touching `ApiClient` again.
 *
 * **Two cases deliberately emit nothing**, so a subscriber counting events is
 * counting round trips that reached the server and came back:
 *
 * 1. A `dedupedFetch` cache hit — nothing reached the network at all.
 * 2. A **rejected** fetch (network failure, DNS, abort). There is no
 *    `Response`, so there is no `status` and no `Server-Timing`; inventing a
 *    null-status event would force every subscriber to handle a shape that
 *    carries no timing attribution anyway. If a future panel needs to surface
 *    network failures, add a distinct event type rather than widening this
 *    one. The rejection itself propagates to the caller unchanged.
 */
export interface ApiTimingEvent {
  /** The endpoint passed to the `ApiClient` call, e.g. `/api/projects/123`. */
  url: string;
  method: string;
  status: number;
  /** Client-observed duration in milliseconds, measured with `performance.now()`. */
  clientMs: number;
  /** Parsed `Server-Timing` split, or `null` when absent/unreadable/malformed. */
  server: ServerTiming | null;
}

type Subscriber = (event: ApiTimingEvent) => void;

const subscribers = new Set<Subscriber>();

/**
 * Subscribe to client-observed API call timings. Returns an unsubscribe
 * function. A throwing subscriber is caught and never breaks the API call
 * that triggered it, nor any other subscriber in the set.
 */
export function subscribe(callback: Subscriber): () => void {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

/**
 * Deliver a timing event to every subscriber. Not intended for use outside
 * `apiClient.ts` — consumers should only ever call `subscribe`.
 */
export function notify(event: ApiTimingEvent): void {
  subscribers.forEach((callback) => {
    try {
      callback(event);
    } catch {
      // A subscriber's failure must never propagate — it would otherwise
      // break the API call being observed and skip every subscriber after it.
    }
  });
}
